import { useCallback, useEffect, useRef, useState } from "react";
import type { TabFilterField } from "@/features/app-studio/api/appTabFiltersApi";
import {
  parsePublishedAppItemsSseChunk,
  type AppItemsStreamAppDetail,
  type PublishedAppItemsStreamEvent,
  type PublishedAppStreamItem,
  type PublishedAppTab,
  type PublishedAppTabSummary,
} from "@/features/published-apps/lib/publishedAppItemsStream";
import { subscribeToPrefetchedAppItemsStream } from "@/features/published-apps/lib/publishedAppItemsPrefetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppItemsStreamStatus =
  | "idle"
  | "connecting"
  | "streaming"
  | "complete"
  | "error"
  | "unavailable";

/**
 * Where to stream from. `key` identifies the subject (an app id or a share code) and drives
 * reset/abort when it changes; `fetcher` decides whether the request is authenticated.
 */
export type AppItemsStreamSource = {
  key: string;
  path: string;
  fetcher: (path: string, init: RequestInit) => Promise<Response>;
};

export type AppItemsStreamResult = {
  items: PublishedAppStreamItem[];
  tabs: PublishedAppTab[];
  tabSummaries: PublishedAppTabSummary[];
  status: AppItemsStreamStatus;
  error: string | null;
  appSummary: string | null;
  /** From the shared stream's opening frame; always null on the published stream. */
  app: AppItemsStreamAppDetail | null;
  /** From the shared stream's `filters` frame; always empty on the published stream. */
  filters: TabFilterField[];
  /** From the `done` frame: a summary tier is stale and a refresh would change its text. */
  summariesStale: boolean;
  /**
   * Replace one already-streamed item in place, matched on `(item_type, id)`. Used by a table
   * page turn, which re-fetches just that one widget rather than reloading the whole app — the
   * same merge-in-place shape the `itemSummary` frame already uses internally.
   */
  updateItem: (item: PublishedAppStreamItem) => void;
  /**
   * Drop one already-streamed item, matched on `(item_type, id)`. Used after a widget is
   * deleted: the streamed list is the source the builder re-merges from, so an item left in it
   * reappears the next time that merge runs.
   */
  removeItem: (itemType: PublishedAppStreamItem["item_type"], id: number) => void;
};

/**
 * Streams an app's widgets over `fetch` + `ReadableStream` rather than `EventSource`, so the caller's
 * `fetcher` can attach tenant (and optionally auth) headers.
 *
 * Widgets are unbounded in number and arrive one at a time as each query finishes — in completion
 * order, not `sort_order` — so callers must re-sort for display. Frames are applied as they land,
 * which is the point: the page paints progressively instead of waiting on the slowest query.
 *
 * A 404 before the stream opens becomes `unavailable` (a shared link that is unknown, expired,
 * revoked, or whose app was unpublished).
 */
export function useAppItemsStream(source: AppItemsStreamSource | null): AppItemsStreamResult {
  const [items, setItems] = useState<PublishedAppStreamItem[]>([]);
  const [tabs, setTabs] = useState<PublishedAppTab[]>([]);
  const [tabSummaries, setTabSummaries] = useState<PublishedAppTabSummary[]>([]);
  const [status, setStatus] = useState<AppItemsStreamStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [appSummary, setAppSummary] = useState<string | null>(null);
  const [app, setApp] = useState<AppItemsStreamAppDetail | null>(null);
  const [filters, setFilters] = useState<TabFilterField[]>([]);
  const [summariesStale, setSummariesStale] = useState(false);
  const activeKeyRef = useRef<string | null>(null);

  const updateItem = useCallback((patch: PublishedAppStreamItem) => {
    setItems((current) =>
      current.map((item) =>
        // Same `item_type` on both sides, so the merge is that one kind; the cast says so, since
        // TypeScript cannot narrow a spread of two union members.
        item.item_type === patch.item_type && item.id === patch.id
          ? ({ ...item, ...patch } as PublishedAppStreamItem)
          : item,
      ),
    );
  }, []);

  const removeItem = useCallback(
    (itemType: PublishedAppStreamItem["item_type"], id: number) => {
      setItems((current) =>
        current.filter((item) => !(item.item_type === itemType && item.id === id)),
      );
    },
    [],
  );

  /**
   * Apply one parsed frame. Shared by the live reader below and by a replay of an already-buffered
   * stream, so a prefetched app hydrates through exactly the same path as a freshly streamed one.
   */
  const applyStreamEvent = useCallback((event: PublishedAppItemsStreamEvent) => {
    if (event.type === "item") {
      setItems((current) => [...current, event.item]);
    } else if (event.type === "itemSummary") {
      // Merged onto the already-streamed item, which arrived with no `summary` key at all.
      // Setting it — even to null — is what ends that widget's "generating" placeholder.
      const { item_type, item_id, summary } = event.itemSummary;
      setItems((current) =>
        current.map((item) =>
          item.item_type === item_type && item.id === item_id ? { ...item, summary } : item,
        ),
      );
    } else if (event.type === "tabs") {
      setTabs(event.tabs);
    } else if (event.type === "app") {
      setApp(event.app);
    } else if (event.type === "filters") {
      setFilters(event.filters);
    } else {
      setStatus("complete");
      setAppSummary(event.appSummary);
      setTabSummaries(event.tabSummaries);
      setSummariesStale(event.summariesStale);
    }
  }, []);

  const sourceKey = source?.key ?? null;
  const sourcePath = source?.path ?? null;
  const fetcher = source?.fetcher ?? null;

  useEffect(() => {
    if (sourceKey === null || sourcePath === null || fetcher === null) {
      activeKeyRef.current = null;
      setItems([]);
      setTabs([]);
      setTabSummaries([]);
      setStatus("idle");
      setError(null);
      setAppSummary(null);
      setApp(null);
      setFilters([]);
      setSummariesStale(false);
      return;
    }

    const abortController = new AbortController();
    activeKeyRef.current = sourceKey;
    setItems([]);
    setTabs([]);
    setTabSummaries([]);
    setStatus("connecting");
    setError(null);
    setAppSummary(null);
    setApp(null);
    setFilters([]);

    const finishStream = (
      outcome: "complete" | "error" | "unavailable",
      streamError: string | null,
    ) => {
      if (activeKeyRef.current !== sourceKey) {
        return;
      }
      if (outcome === "error") {
        setStatus("error");
        setError(streamError ?? "Unable to load charts and tables.");
        return;
      }
      setStatus(outcome);
    };

    // A favourite warmed at sign-in is already buffered, so the frames are replayed from memory and
    // no second request is made. Still-running prefetches are attached to rather than duplicated,
    // which keeps the progressive paint: remaining widgets arrive as the server finishes them.
    const prefetched = subscribeToPrefetchedAppItemsStream(sourceKey, {
      onEvent: applyStreamEvent,
      onSettled: finishStream,
    });
    if (prefetched) {
      if (prefetched.outcome === "streaming") {
        setStatus("streaming");
      } else {
        finishStream(prefetched.outcome, prefetched.error);
      }
      return () => {
        prefetched.unsubscribe();
        if (activeKeyRef.current === sourceKey) {
          activeKeyRef.current = null;
        }
      };
    }

    const consumeStream = async () => {
      try {
        const response = await fetcher(sourcePath, {
          method: "GET",
          headers: { Accept: "text/event-stream" },
          signal: abortController.signal,
        });

        if (response.status === 404) {
          if (activeKeyRef.current === sourceKey) {
            setStatus("unavailable");
          }
          return;
        }
        if (!response.ok) {
          throw new Error(await readApiErrorMessage(response));
        }
        if (!response.body) {
          throw new Error("Stream response did not include a body.");
        }
        if (activeKeyRef.current !== sourceKey) {
          return;
        }

        setStatus("streaming");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const parsed = parsePublishedAppItemsSseChunk(buffer);
          buffer = parsed.remainder;

          for (const event of parsed.events) {
            applyStreamEvent(event);
          }
        }

        if (activeKeyRef.current === sourceKey) {
          setStatus("complete");
        }
      } catch (streamError) {
        if (abortController.signal.aborted) {
          return;
        }
        setStatus("error");
        setError(
          streamError instanceof Error
            ? streamError.message
            : "Unable to load charts and tables.",
        );
      }
    };

    void consumeStream();

    return () => {
      abortController.abort();
      if (activeKeyRef.current === sourceKey) {
        activeKeyRef.current = null;
      }
    };
  }, [sourceKey, sourcePath, fetcher, applyStreamEvent]);

  return {
    items,
    tabs,
    tabSummaries,
    status,
    error,
    appSummary,
    app,
    filters,
    summariesStale,
    updateItem,
    removeItem,
  };
}
